import type { PictureImage } from '@vs-shop/image-link/Picture';
import type { Banner } from '../effect/schema/openapi/backend';
import type { ImageWithFallback } from '../type/imagePath';

import { Effect, Option, pipe, Record } from 'effect';

import { getBannerImagePaths } from './getBannerImagePaths';
import { resolveImageUrlEffect } from '@vs-shop/engine/image/resolveImageUrl';
import { getImagesWithFallback, getImageWithFallback } from './imageFallback';

import BannerImageFallback from '../assets/banner/banner_fallback.png';
import {
  BannerVariant,
  type BannerImageProps,
  type BannerVariant as BannerVariantT,
} from '@vs-shop/commerce-ui/banner';
import { RuntimeClientConfig } from '@vs-shop/engine/runtime/RuntimeClientConfig';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

/**
 * The app side of the shared media contract: **resolution**, which is the half
 * `@vs-shop/commerce-ui` deliberately cannot do.
 *
 * `{filter}` templating, the GCS/API environment split, the
 * desktop→tablet→mobile borrowing and the backend retry URLs all happen here,
 * behind rcprofi's Effect config runtime. What comes out is final renderable
 * URLs plus the brand's terminal asset — nothing the section has to interpret.
 *
 * The two media queries are rcprofi's own thresholds, matching the `lap` and
 * `tab` steps of its theme. They are written out because a media query is a raw
 * string in markup: a Tailwind breakpoint name cannot appear inside one.
 */
const DESKTOP_MEDIA = '(min-width: 1024px)';
const TABLET_MEDIA = '(min-width: 640px)';

type BannerMediaBanner = Pick<
  Banner,
  'imageDesktopAbstract' | 'imageTabletAbstract' | 'imageMobilAbstract'
>;

/**
 * `Option`s out, plain nullable strings in.
 *
 * The section's media renders in a `'use client'` component, and an `Option` is
 * an Effect class instance — which RSC rejects at that boundary. Plainifying
 * here is what makes the pair crossable.
 */
const toPlain = (image: ImageWithFallback): PictureImage => ({
  primary: Option.getOrNull(image.primary),
  backendFallback: Option.getOrNull(image.backendFallback),
});

export function getBannerMediaProps(
  banner: BannerMediaBanner,
  variant: BannerVariantT
): Omit<BannerImageProps, 'alt'> {
  // The homepage band is not art-directed: one wide crop, cut by the CMS at a
  // filter of its own, shown at every device class. Offering `<source>`s that
  // all name the same URL would be three ways to load one image, so the hero
  // hands the part a single image and it renders a plain `<img>`.
  if (variant === BannerVariant.HERO) {
    const image = RuntimeClientConfig.runSync(
      pipe(
        Option.fromNullishOr(banner.imageDesktopAbstract),
        Option.match({
          onNone: () => Effect.succeed(Option.none<string>()),
          onSome: (pathAbstract) =>
            resolveImageUrlEffect(pathAbstract, ImageFilter.banner_homepage_desktop_image).pipe(
              Effect.asSome
            ),
        }),
        Effect.flatMap(getImageWithFallback)
      )
    );

    return { image: toPlain(image), terminalFallbackSrc: BannerImageFallback.src };
  }

  const images = RuntimeClientConfig.runSync(
    pipe(
      {
        desktop: banner.imageDesktopAbstract,
        tablet: banner.imageTabletAbstract,
        mobile: banner.imageMobilAbstract,
      },
      Record.map(Option.fromNullishOr),
      getBannerImagePaths(variant),
      Effect.flatMap(getImagesWithFallback)
    )
  );

  return {
    sources: [
      { media: DESKTOP_MEDIA, image: toPlain(images.desktop) },
      { media: TABLET_MEDIA, image: toPlain(images.tablet) },
    ],
    image: toPlain(images.mobile),
    terminalFallbackSrc: BannerImageFallback.src,
  };
}
